import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { resumeApi } from '../../services/resumeApi';
import { resumeUploadService } from '../../services/resumeUpload.service';
import ResumesContent from '../../components/common/table/ResumesTable';
import UploadResumeContent from '../../components/common/resume/UploadResumeContent';
import ProcessingStatusBadge from '../../components/common/resume/ProcessingStatusBadge';
import FetchResumesModal from '../../components/common/modal/FetchResumesModal';
import { FileUp, Download, Settings, ChevronDown, ChevronUp, Save, Mail, CheckSquare, Square, X, Loader2, Eye } from 'lucide-react';
import BulkPreviewModal from '../../components/common/modal/BulkPreviewModal';
import ResumeUploadModal from '../../components/common/resume/ResumeUpload';
import { toast } from 'react-toastify';
import api from '../../services/api';

const ResumeManager = () => {
  const { id: jobId } = useParams();
  const [resumes, setResumes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [totalResumes, setTotalResumes] = useState(0);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    totalPages: 1,
    limit: 10
  });
  const [currentFilter, setCurrentFilter] = useState('all');
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
    isUploading: false,
    message: ''
  });

  // Bulk invite state
  const [selectedIds,    setSelectedIds]    = useState([]);
  const [bulkStageId,    setBulkStageId]    = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [previewing,     setPreviewing]     = useState(false);
  const [previewData,    setPreviewData]    = useState(null); // { drafts, stage, job }

  // Fetch resumes modal state
  const [showFetchResumesModal, setShowFetchResumesModal] = useState(false);
  const [fetchingResumes, setFetchingResumes] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Screening thresholds
  const [thresholds, setThresholds] = useState({ jdMatchThreshold: 80, atsScoreThreshold: 85 });
  const [thresholdOpen, setThresholdOpen] = useState(false);
  const [savingThresholds, setSavingThresholds] = useState(false);

  const [jobPipelineStages, setJobPipelineStages] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    api.get(`/jobs/${jobId}`).then(r => {
      const data = r.data?.data || r.data || {};
      const cfg  = data.screeningConfig || {};
      setThresholds({
        jdMatchThreshold:  cfg.jdMatchThreshold  ?? 80,
        atsScoreThreshold: cfg.atsScoreThreshold ?? 85,
      });
      // Populate stage picker from job pipeline so ALL stages are available
      const pipeline = (cfg.pipeline || []).filter(s => s.enabled !== false);
      setJobPipelineStages(pipeline.map(s => ({ stageId: s.stageId, label: s.label || s.type })));
    }).catch(() => {});
  }, [jobId]);

  const saveThresholds = async () => {
    setSavingThresholds(true);
    try {
      const resp = await api.patch(`/jobs/${jobId}/screening-thresholds`, thresholds);
      const updated = resp.data?.updated ?? 0;
      toast.success(`Thresholds saved. ${updated} existing candidate${updated !== 1 ? 's' : ''} re-evaluated.`);
      setThresholdOpen(false);
      fetchResumes();
    } catch (e) {
      toast.error('Failed to save thresholds');
    } finally {
      setSavingThresholds(false);
    }
  };

  // Reprocess functions
  const handleReprocessResume = async (resumeId) => {
    try {
      console.log('Reprocessing resume with job context:', { resumeId, jobId });
      const result = await resumeApi.reprocessResume(resumeId, jobId);
      console.log('Reprocess result:', result);
      // Refresh the resumes list
      fetchResumes();
      return result;
    } catch (error) {
      console.error('Error reprocessing resume:', error);
      // Show user-friendly error message
      toast.error(error.message || 'Failed to reprocess resume');
      throw error;
    }
  };

  const handleReprocessAll = async () => {
    try {
      const result = await resumeApi.reprocessAllResumes(jobId);
      console.log('Reprocess all result:', result);
      // Refresh the resumes list
      fetchResumes();
      return result;
    } catch (error) {
      console.error('Error reprocessing all resumes:', error);
      // Show user-friendly error message
      toast.error(error.message || 'Failed to reprocess all resumes');
      throw error;
    }
  };


  const fetchResumes = async (page = 1, filter = currentFilter) => {
    try {
      console.log('Fetching resumes for jobId:', jobId);
      setIsLoading(true);
      const response = await resumeApi.getAllResumes(jobId, page, pagination.limit, filter);
      console.log('Resumes response:', response);
      console.log('Resumes found:', response.data.resumes?.length || 0);
      
      setResumes(response.data.resumes || []);
      setTotalResumes(response.data.totalResumes);
      setPagination({
        total: response.data.total,
        page: response.data.page || 1,
        totalPages: response.data.totalPages || 1,
        limit: pagination.limit,
        statusCounts: response.data.statusCounts
      });
      setCurrentFilter(filter);
    } catch (error) {
      console.error('Error fetching resumes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchResumes(1, 'all');
    }
  }, [jobId]);

  // Auto-refresh every 5s while any resume is still being processed
  useEffect(() => {
    const hasProcessing = resumes.some(r =>
      r.processingStatus === 'uploaded' ||
      r.processingStatus === 'queued' ||
      r.processingStatus === 'processing'
    );
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      fetchResumes(pagination.page, currentFilter);
    }, 15000);

    return () => clearInterval(interval);
  }, [resumes, pagination.page, currentFilter]);

  const handleUpload = async (files) => {
    try {

      setUploadProgress({
        current: 0,
        total: files.length,
        isUploading: true,
        message: 'Starting upload...'
      });
      
      setIsModalOpen(false);
      
      // Use the new batch upload service
      const uploadResult = await resumeUploadService.uploadResumesBatch(jobId, files, (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          current: Math.round((progress.progress / 100) * files.length),
          message: progress.message || 'Uploading files...'
        }));
      });
      
      // Refresh the resumes list after batch upload
      await fetchResumes(1, currentFilter);
      
      // Show success message
      if (uploadResult.success) {
        const successCount = uploadResult.uploadedFiles?.length || 0;
        const failedCount = uploadResult.failedFiles?.length || 0;
        
        if (failedCount > 0) {
          console.warn(`${successCount} files uploaded successfully, ${failedCount} failed`);
        } else {
          console.log(`${successCount} files uploaded successfully`);
        }
      }
      
    } catch (error) {
      console.error('Error uploading resumes:', error);
      toast.error(`Upload failed: ${error.message}`);
      // Still refresh — server may have partially succeeded
      try { await fetchResumes(1, currentFilter); } catch (_) {}
    } finally {
      setUploadProgress({
        current: 0,
        total: 0,
        isUploading: false,
        message: ''
      });
    }
  };

  const handleStatusChange = async (resumeId, type, status, override = false) => {
    try {
      await resumeApi.updateResumeStatus(resumeId, type, status, override);
      fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error updating resume status:', error);
      toast.error('Failed to update resume status');
    }
  };

  const handleDelete = async (resumeId) => {
    try {
      await resumeApi.deleteResume(resumeId);
      fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error deleting resume:', error);
      toast.error('Failed to delete resume');
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await fetchResumes(pagination.page, currentFilter);
    } catch (error) {
      console.error('Error refreshing resumes:', error);
      toast.error('Failed to refresh resumes');
    } finally {
      setRefreshing(false);
    }
  };

  // Derive pipeline stages from resumes' pipelineStageResults for the stage picker
  const pipelineStages = React.useMemo(() => {
    const seen = new Map();
    resumes.forEach(r => {
      (r.pipelineStageResults || []).forEach(s => {
        if (!seen.has(s.stageId)) seen.set(s.stageId, s.label || s.stageId);
      });
    });
    return [...seen.entries()].map(([stageId, label]) => ({ stageId, label }));
  }, [resumes]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === resumes.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(resumes.map(r => r._id));
    }
  };

  // Step 1 — generate drafts and open preview modal
  const handlePreviewInvites = async () => {
    if (!bulkStageId) { toast.error('Select a pipeline stage first'); return; }
    if (selectedIds.length === 0) { toast.error('Select at least one candidate'); return; }

    setPreviewing(true);
    try {
      const res = await api.post('/invites/bulk/preview', {
        resumeIds: selectedIds,
        stageId:   bulkStageId,
        jobId,
      });
      setPreviewData(res.data.data); // { drafts, stage, job }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate email previews');
    } finally {
      setPreviewing(false);
    }
  };

  // Step 2 — HR approved, fire the actual sends with (possibly edited) drafts
  const handleApproveAndSend = async (approvedDrafts) => {
    setSendingInvites(true);
    try {
      const res = await api.post('/invites/bulk', {
        resumeIds: approvedDrafts.map(d => d.resumeId),
        stageId:   bulkStageId,
        jobId,
        drafts:    approvedDrafts, // pre-generated subjects + bodies
      });
      const { sent, failed, total } = res.data.data;
      toast.success(`Sent ${sent}/${total} invite emails${failed > 0 ? ` (${failed} failed)` : ''}`);
      setPreviewData(null);
      setSelectedIds([]);
      setBulkStageId('');
      fetchResumes(pagination.page, currentFilter);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Bulk invite failed');
    } finally {
      setSendingInvites(false);
    }
  };

  const handleImportOrganizationResumes = async (selectedResumes) => {
    try {
      setFetchingResumes(true);


      let successCount = 0;
      let errorCount = 0;
      const errors = [];

      console.log('Starting import of', selectedResumes.length, 'resumes');

      // Process each selected resume
      for (const resume of selectedResumes) {
        try {
          console.log('Processing resume:', resume.candidateName, resume.email);
          
          // Check if resume already exists for this job (email duplication check)
          const existingResume = resumes.find(r => r.email === resume.email);
          
          if (existingResume) {
            console.log(`Resume with email ${resume.email} already exists for this job`);
            continue; // Skip this resume
          }

          // Prepare resume data for import
          const resumeData = {
            jobId: resume.jobId, // Original job ID
            fileName: resume.fileName,
            fileUrl: resume.fileUrl,
            content: resume.content || '',
            email: resume.email,
            candidateName: resume.candidateName,
            phoneNumber: resume.phoneNumber || '',
            aiScore: resume.aiScore || 0,
            matchScore: resume.matchScore || 0,
            analysis: resume.analysis || {},
            status: resume.resumeScreeningStatus || 'not_attended'
          };

          console.log('Importing resume with data:', resumeData);

          // Create new resume entry for this job (without uploading to blob storage)
          const result = await resumeApi.importResumeFromOrganization(jobId, resumeData);
          
          if (result.success) {
            successCount++;
            console.log(`Successfully imported resume: ${resume.candidateName}`);
          } else {
            errorCount++;
            errors.push(`Failed to import ${resume.candidateName}: ${result.message}`);
            console.error(`Failed to import resume ${resume.candidateName}:`, result.message);
          }
          
        } catch (error) {
          errorCount++;
          errors.push(`Error importing ${resume.candidateName}: ${error.message}`);
          console.error(`Error importing resume ${resume.candidateName}:`, error);
        }
      }

      console.log(`Import completed: ${successCount} successful, ${errorCount} failed`);

      // Show results to user
      if (successCount > 0) {
        toast.success(`Successfully imported ${successCount} resumes`);
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to import ${errorCount} resumes. Check console for details.`);
        console.error('Import errors:', errors);
      }

      // Refresh the resumes list after a short delay to allow processing
      setTimeout(async () => {
        await fetchResumes(pagination.page, currentFilter);
      }, 2000);
      
    } catch (error) {
      console.error('Error importing organization resumes:', error);
      toast.error('Failed to import some resumes');
      toast.error('Failed to import resumes');
    } finally {
      setFetchingResumes(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  const handlePageChange = (newPage, filter = currentFilter) => {
    setCurrentFilter(filter);
    fetchResumes(newPage, filter);
  };

  if (!isLoading && resumes.length === 0 && currentFilter === 'all') {
    return (
      <UploadResumeContent
        jobId={jobId}
        onUpload={handleUpload}
      />
    );
  }

  // Filter returned no results — show an empty state instead of upload screen
  if (!isLoading && resumes.length === 0 && currentFilter !== 'all') {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 font-medium">
              HR
            </div>
            <h2 className="ml-4 text-xl font-semibold">Resumes</h2>
          </div>
          <div className="flex items-center space-x-4">
            {['all', 'shortlisted', 'rejected'].map((filter) => (
              <button
                key={filter}
                className={`px-4 py-2 rounded transition-colors ${
                  currentFilter === filter
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
                onClick={() => handlePageChange(1, filter)}
              >
                {filter === 'rejected' ? 'Not Shortlisted' :
                 filter === 'shortlisted' ? 'Shortlisted' : 'All'}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <p className="text-lg font-medium">No resumes found</p>
          <p className="text-sm mt-1">No candidates match the selected filter.</p>
          <button
            className="mt-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors text-gray-600"
            onClick={() => handlePageChange(1, 'all')}
          >
            View all resumes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {uploadProgress.isUploading && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-sm">
          <div className="flex justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-500 border-t-transparent"></div>
              <span className="font-medium">
                {uploadProgress.message || 'Uploading resumes'}
                <span className="text-xs text-red-500 ml-2">(Do Not Close This Page)</span>
              </span>
            </div>
            <span className="text-blue-600 font-medium">
              {uploadProgress.current} of {uploadProgress.total}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-500 font-medium">
            HR
          </div>
          <h2 className="ml-4 text-xl font-semibold">Resumes</h2>
        </div>
        
        <div className="flex items-center space-x-4">
          {['all', 'shortlisted', 'rejected'].map((filter) => (
            <button
              key={filter}
              className={`px-4 py-2 rounded transition-colors ${
                currentFilter === filter
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
              onClick={() => handlePageChange(1, filter)}
            >
              {filter === 'rejected' ? 'Not Shortlisted' :
               filter === 'shortlisted' ? 'Shortlisted' : 'All'}
            </button>
          ))}
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
            disabled={uploadProgress.isUploading}
          >
            <FileUp className="w-4 h-4" />
            Upload More
          </button>

          <button
            onClick={() => setShowFetchResumesModal(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2"
            disabled={fetchingResumes}
          >
            <Download className="w-4 h-4" />
            Fetch Resumes
          </button>
        </div>
      </div>

      {/* Processing Status Badge */}
      <div className="mb-4 flex justify-end">
        <ProcessingStatusBadge jobId={jobId} />
      </div>

      {/* Screening Threshold Settings */}
      <div className="mb-6 border border-orange-100 rounded-xl overflow-hidden">
        <button
          onClick={() => setThresholdOpen(o => !o)}
          className="w-full flex items-center justify-between px-4 py-3 bg-orange-50 hover:bg-orange-100 transition-colors text-sm"
        >
          <div className="flex items-center gap-2 font-semibold text-orange-700">
            <Settings className="w-4 h-4" />
            Screening Thresholds
            <span className="font-normal text-orange-500 text-xs">
              (JD Match ≥ {thresholds.jdMatchThreshold}% · ATS ≥ {thresholds.atsScoreThreshold}%)
            </span>
          </div>
          {thresholdOpen ? <ChevronUp className="w-4 h-4 text-orange-400" /> : <ChevronDown className="w-4 h-4 text-orange-400" />}
        </button>
        {thresholdOpen && (
          <div className="px-4 py-4 bg-white border-t border-orange-100">
            <p className="text-xs text-slate-500 mb-3">
              A resume is shortlisted only if BOTH thresholds are met. Saving will immediately re-evaluate all existing candidates for this job.
            </p>
            <div className="flex flex-wrap gap-6 items-end">
              {[
                { key: 'jdMatchThreshold',  label: 'JD Match %' },
                { key: 'atsScoreThreshold', label: 'ATS Score %' },
              ].map(({ key, label }) => (
                <div key={key} className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-slate-600">{label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={thresholds[key]}
                      onChange={e => setThresholds(t => ({ ...t, [key]: Number(e.target.value) }))}
                      className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-orange-300"
                    />
                    <span className="text-slate-400 text-xs">/ 100</span>
                  </div>
                </div>
              ))}
              <button
                onClick={saveThresholds}
                disabled={savingThresholds}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
              >
                <Save className="w-3.5 h-3.5" />
                {savingThresholds ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Select-all row */}
      <div className="flex items-center gap-3 mb-2 px-1">
        <button
          onClick={toggleSelectAll}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-meraki-indigo transition-colors"
        >
          {selectedIds.length === resumes.length && resumes.length > 0
            ? <CheckSquare className="w-4 h-4 text-meraki-indigo" />
            : <Square className="w-4 h-4" />}
          {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
        </button>
        {selectedIds.length > 0 && (
          <button onClick={() => setSelectedIds([])} className="text-xs text-slate-400 hover:text-slate-600">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <ResumesContent
        resumes={resumes}
        pagination={pagination}
        setPagination={setPagination}
        onPageChange={handlePageChange}
        currentFilter={currentFilter}
        screenType="resumeScreening"
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        onReprocess={handleReprocessResume}
        onReprocessAll={handleReprocessAll}
        onRefresh={() => fetchResumes(pagination.page, currentFilter)}
        loading={isLoading}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
      />

      {/* Floating bulk invite bar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-meraki-indigo text-white px-5 py-3 rounded-2xl shadow-2xl shadow-indigo-900/30">
          <Mail className="w-4 h-4 opacity-80" />
          <span className="text-sm font-semibold">{selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''} selected</span>
          <span className="text-indigo-300">→</span>
          <select
            value={bulkStageId}
            onChange={e => setBulkStageId(e.target.value)}
            className="bg-white/10 border border-white/20 text-white text-sm rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-white/40"
          >
            <option value="">Pick a stage…</option>
            {(jobPipelineStages.length ? jobPipelineStages : pipelineStages).map(s => (
              <option key={s.stageId} value={s.stageId}>{s.label}</option>
            ))}
          </select>
          <button
            onClick={handlePreviewInvites}
            disabled={previewing || !bulkStageId}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-white text-meraki-indigo rounded-lg text-sm font-bold hover:bg-indigo-50 transition-colors disabled:opacity-50"
          >
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
            {previewing ? 'Drafting…' : 'Preview & Send'}
          </button>
          <button onClick={() => setSelectedIds([])} className="text-white/60 hover:text-white ml-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {previewData && (
        <BulkPreviewModal
          drafts={previewData.drafts}
          stage={previewData.stage}
          job={previewData.job}
          onApprove={handleApproveAndSend}
          onClose={() => setPreviewData(null)}
          sending={sendingInvites}
        />
      )}

      <ResumeUploadModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpload={handleUpload}
        accept=".pdf,.docx"
        isUploading={uploadProgress.isUploading}
        jobId={jobId}
        onBankAttached={() => { setIsModalOpen(false); fetchResumes(pagination.page, currentFilter); }}
      />

      <FetchResumesModal
        isOpen={showFetchResumesModal}
        onClose={() => setShowFetchResumesModal(false)}
        jobId={jobId}
        // FetchResumesModal now POSTs to /resumes/bank/attach itself and
        // calls back with the COUNT of attached resumes (a number, not an
        // array). The legacy `handleImportOrganizationResumes` flow that
        // iterated an array of resumes is gone — we just refresh the table.
        onResumesSelected={() => {
          setShowFetchResumesModal(false);
          fetchResumes(pagination.page, currentFilter);
        }}
      />
    </div>
  );
  };
  
export default ResumeManager; 